"use client"

import { usePathname } from "next/navigation"
import { Section } from "@/components/layout/Section"
import { Button } from "@/components/ui/button"
import { Palette, CheckCircle2, FileText, Calendar, Code2, Rocket, Mail, Phone, ArrowRight } from "lucide-react"
import Link from "next/link"
import Image from "next/image"
import { siteConfig } from "@/config/site"
import { CTA } from "@/components/sections/CTA"
import { ServiceReferences } from "@/components/sections/ServiceReferences"
import { type Locale } from "@/lib/i18n"

const features = [
  "Modern ve kullanıcı dostu tasarımlar",
  "Google'da üst sıralarda görünme",
  "Telefon, tablet ve bilgisayarda mükemmel görünüm",
  "Saniyeler içinde açılan hızlı sayfalar",
  "Markanıza özel, benzersiz tasarım çözümleri",
  "Müşterilerinizin kolayca kullanabileceği arayüzler",
]

const steps = [
  {
    icon: FileText,
    title: "Teklif Al",
    description: "Projeniz için ücretsiz teklif alın ve ihtiyaçlarınızı paylaşın",
  },
  {
    icon: Calendar,
    title: "Planla",
    description: "Proje planını birlikte oluşturalım ve zaman çizelgesini belirleyelim",
  },
  {
    icon: Code2,
    title: "Geliştir",
    description: "Profesyonel ekibimiz projenizi geliştirirken sizi bilgilendiriyoruz",
  },
  {
    icon: Rocket,
    title: "Yayınla",
    description: "Projeniz hazır! Yayınlıyoruz ve destek sağlıyoruz",
  },
]

export default function WebDesignPage() {
  const pathname = usePathname()
  const currentLocale: Locale = pathname?.startsWith('/en/') ? 'en' : 'tr'
  
  return (
    <>
      <Section className="bg-gradient-to-br from-[rgb(3,74,166)] to-[rgb(2,40,89)] text-white py-12 sm:py-16 lg:py-24">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex p-4 bg-white/10 rounded-lg mb-6">
            <Palette className="h-12 w-12" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl mb-6">
            Web Tasarım
          </h1>
          <p className="text-lg text-white/90 sm:text-xl">
            Modern, kullanıcı odaklı ve SEO uyumlu web tasarımları ile markanızı dijital dünyada öne çıkarın
          </p>
        </div>
      </Section>

      <Section className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto">
          {/* Görsel ve Açıklama */}
          <div className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-8">
              <div className="relative h-[300px] lg:h-[400px] bg-slate-100">
                <Image
                  src="/web-design.jpg"
                  alt="Web Tasarım"
                  fill
                  className="object-cover"
                />
                <div className="absolute inset-0 bg-[rgb(3,74,166)]/30"></div>
              </div>
              <div className="flex flex-col justify-center">
                <h2 className="text-3xl font-bold mb-6">Profesyonel Web Tasarım Hizmetleri</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Markanızın dijital kimliğini yansıtan, dönüşüm oranlarını artıran profesyonel web siteleri tasarlıyoruz. 
                  Modern tasarım prensipleri ve kullanıcı deneyimi odaklı yaklaşımımızla, ziyaretçilerinizi etkileyecek 
                  ve iş hedeflerinize ulaşmanızı sağlayacak web siteleri oluşturuyoruz.
                </p>
                <p className="text-lg text-muted-foreground">
                  Her projede, markanızın benzersiz değerlerini ve müşterilerinizin ihtiyaçlarını anlayarak, 
                  özel tasarım çözümleri geliştiriyoruz. Google'da üst sıralarda görünme, telefon ve tablette 
                  mükemmel görünüm ve hızlı açılma gibi özellikleri de düşünerek, hem görsel hem de 
                  kullanım açısından mükemmel web siteleri tasarlıyoruz.
                </p>
              </div>
            </div>
            <p className="text-lg text-muted-foreground">
              Web tasarım sürecimiz, markanızın müşterilerini anlamakla başlar. Müşteri araştırmaları, 
              rakip analizleri ve sektör trendlerini inceleyerek, markanıza özel bir tasarım stratejisi 
              geliştiriyoruz. Tüm cihazlarda mükemmel görünen, hızlı açılan ve müşterilerinizin kolayca 
              kullanabileceği web siteleri oluşturuyoruz. Google'da üst sıralarda görünmeniz için 
              gerekli teknik altyapıyı da tasarım aşamasında düşünerek, hem görsel hem de performans 
              açısından üstün web siteleri tasarlıyoruz.
            </p>
          </div>

          {/* Özellikler */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold mb-6">Özellikler</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-[rgb(3,74,166)] mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Çalışma Aşamaları */}
          <div className="mb-12">
            <div>
              {/* Başlık */}
              <div className="text-center mb-12">
                <h3 className="text-3xl lg:text-4xl font-bold">
                  <span className="text-slate-900">Kanıtlanmış</span>{" "}
                  <span className="text-[rgb(3,74,166)]">Çalışma Sürecimiz</span>
                </h3>
              </div>

              {/* Aşamalar */}
              <div className="relative">
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
                  {steps.map((step, index) => {
                    const Icon = step.icon
                    const stepNumber = String(index + 1).padStart(2, '0')
                    return (
                      <div key={index} className="relative">
                        {/* Bağlantı çizgisi (son adım hariç) */}
                        {index < steps.length - 1 && (
                          <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-slate-300 -z-10" 
                               style={{ width: 'calc(100% - 80px)', left: 'calc(50% + 40px)' }} />
                        )}
                        
                        <div className="text-center">
                          {/* İkon ve Numara */}
                          <div className="relative inline-block mb-6">
                            {/* Mavi daire - İkon */}
                            <div className="w-16 h-16 rounded-full bg-[rgb(3,74,166)] flex items-center justify-center">
                              <Icon className="h-7 w-7 text-white" />
                            </div>
                            {/* Numara - Sağ üstte */}
                            <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-slate-700 border-2 border-white flex items-center justify-center">
                              <span className="text-white text-xs font-bold">{stepNumber}</span>
                            </div>
                          </div>
                          
                          {/* Başlık */}
                          <h4 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h4>
                          
                          {/* Açıklama */}
                          <p className="text-sm text-slate-600 leading-relaxed">{step.description}</p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>
      <ServiceReferences serviceSlug="web-tasarim" />
      <CTA locale={currentLocale} />
    </>
  )
}

